/**
 * TokenBalance Component (v2.0 - Platform Consistent)
 * Displays user's Q AI token balance with visual states
 * Uses platform green color instead of purple
 */

import React from 'react';
import { Chip, Typography, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';

/**
 * TokenBalance Component
 * @param {number} balance - Current token balance
 * @param {boolean} isLow - Whether balance is low (<=15)
 * @param {function} onBuyTokens - Callback when user wants to buy tokens
 * @param {string} size - 'small' | 'medium' (chip size)
 */
const TokenBalance = ({
  balance = 0,
  isLow = false,
  onBuyTokens,
  size = 'medium'
}) => {
  // Get background color for chip - platform consistent
  const getBgColor = () => {
    if (balance === 0) return 'rgba(237, 76, 92, 0.1)';
    if (isLow || balance <= 15) return 'rgba(245, 158, 11, 0.1)';
    return 'rgba(39, 181, 109, 0.1)';
  };

  // Get text color - platform consistent
  const getTextColor = () => {
    if (balance === 0) return 'rgba(237, 76, 92, 1)';
    if (isLow || balance <= 15) return '#F59E0B';
    return 'rgba(39, 181, 109, 1)';
  };

  // Get tooltip message
  const getTooltip = () => {
    if (balance === 0) return 'No tokens remaining. Click to buy more.';
    if (isLow || balance <= 15) return 'Low balance. Click to buy more tokens.';
    return `${balance} tokens available`;
  };

  const isClickable = (isLow || balance <= 15 || balance === 0) && onBuyTokens;

  return (
    <Tooltip title={getTooltip()} arrow placement="bottom">
      <Chip
        icon={
          <BoltIcon
            sx={{
              color: `${getTextColor()} !important`,
              fontSize: size === 'small' ? 14 : 18
            }}
          />
        }
        label={
          <Typography
            sx={{
              fontFamily: "'Roboto Mono', monospace",
              fontWeight: 600,
              fontSize: size === 'small' ? '0.6875rem' : '0.8125rem',
              color: getTextColor(),
              whiteSpace: 'nowrap'
            }}
          >
            {balance}
          </Typography>
        }
        onClick={isClickable ? onBuyTokens : undefined}
        sx={{
          backgroundColor: getBgColor(),
          border: `1px solid ${getTextColor()}30`,
          borderRadius: '6px',
          height: size === 'small' ? 26 : 32,
          flexShrink: 0,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': isClickable
            ? {
                backgroundColor: getBgColor(),
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${getTextColor()}30`
              }
            : {},
          '& .MuiChip-icon': {
            marginLeft: '6px',
            marginRight: '-4px'
          },
          '& .MuiChip-label': {
            paddingLeft: '6px',
            paddingRight: '10px'
          }
        }}
      />
    </Tooltip>
  );
};

export default TokenBalance;
